"use client";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getLanguage, useTranslation } from "@/lib/i18n";
import { Lng } from "@/lib/i18n/settings";
import { getDefaultSections, SectionItem } from "@/lib/schema/sections";
import { useResumeStore } from "@/lib/store/resume";
import { cn } from "@/lib/utils";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { createId } from "@paralleldrive/cuid2";
import { produce } from "immer";
import { get } from "lodash";
import { ElementType, ReactNode, useState } from "react";
import Header from "../../shared/Header";
import SortItem from "./SortItem";

type TProps = {
  id: "workExperience" | "projects" | "skills" | "education" | string;
  title: string;
  itemTitle: string;
  ItemContent: ElementType;
  icon: ReactNode;
  isEnableVisibilitySwitch?: Boolean;
  isCustomSection?: Boolean;
};

const Base = <T extends SectionItem>(props: TProps) => {
  const { t } = useTranslation({ ns: "resume-sidebar" });
  const {
    icon,
    id,
    title,
    itemTitle,
    ItemContent,
    isEnableVisibilitySwitch = true,
    isCustomSection = false,
  } = props;
  const { setValue, resume } = useResumeStore();
  const [newItemIds, setNewItemIds] = useState<string[]>([]);
  const language = resume?.language || getLanguage();
  const sections =
    get(resume.data.sections, id) ||
    get(getDefaultSections(language as Lng), id);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!get(resume.data.sections, id) && sections) {
    setValue(`sections.${id}`, sections);
  }

  const items = sections?.items || [];

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex(
        (item: { id: string }) => item.id === active.id,
      );
      const newIndex = items.findIndex(
        (item: { id: string }) => item.id === over.id,
      );

      const sortedList = arrayMove(items as any[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  const toggleVisibility = () =>
    setValue(`sections.${id}.visible`, !sections.visible);

  return (
    <div
      className="group"
      id={isCustomSection ? `builder-custom-${id}` : `builder-${id}`}
    >
      <div
        className={cn(
          !sections?.visible && "opacity-50",
          "mb-2 flex w-full items-center justify-between",
        )}
      >
        <Header icon={icon} title={sections?.name} id={id} />

        <div className="flex items-center gap-2">
          {isEnableVisibilitySwitch && (
            <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
              <Switch
                checked={sections?.visible}
                onCheckedChange={toggleVisibility}
              ></Switch>
            </div>
          )}
          <Button
            size="sm"
            variant={"outline"}
            onClick={() => {
              const itemId = createId();
              setNewItemIds([...newItemIds, itemId]);
              setValue(
                `sections.${id}.items`,
                produce(items as unknown as string, (draft: any[]): void => {
                  draft.push({
                    id: itemId,
                    visible: true,
                    [itemTitle]: title,
                  });
                }),
              );
            }}
          >
            {t("left.add")}
          </Button>
        </div>
      </div>

      <div
        className={cn(
          !sections?.visible && "opacity-50",
          "flex flex-col gap-2",
        )}
      >
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((res: any) => (
              <SortItem
                data={res}
                key={res.id}
                id={res.id}
                itemTitle={itemTitle}
                ItemContent={ItemContent}
                sectionId={id}
                newItemIds={newItemIds}
                isCustomSection={isCustomSection}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default Base;
